import React, { useState, useEffect, useRef } from "react";
import useAxios from "../../hooks/useAxios";
import { useAuth } from "../../contexts/AuthContext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Toolbar } from "primereact/toolbar";

const Temperature = () => {
  const { user } = useAuth();
  const { response, loading, fetchData } = useAxios();

  const [data, setData] = useState([]);
  const [date1, setDate1] = useState(null);
  const [date2, setDate2] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    btsName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    region: { value: null, matchMode: FilterMatchMode.CONTAINS },
    temp: { value: null, matchMode: FilterMatchMode.CONTAINS },
    humidity: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const toast = useRef(null);
  const dt = useRef(null);

  useEffect(() => {
    if (!response) return;

    if (response.status === "success" && response.data) {
      setData(response.data);
    } else if (response.status === "error") {
      toast.current.show({
        severity: "error",
        detail: response.message,
        life: 3000,
      });
      setData([]);
    }
  }, [response]);

  // Load default data on mount (last 7 days)
  useEffect(() => {
    fetchData({ method: "get", url: "/api/temperature" });
  }, []);

  const handleSearch = () => {
    const params = {};
    if (date1) {
      params.date1 = formatDate(date1);
    }
    if (date2) {
      params.date2 = formatDate(date2);
    }
    fetchData({ method: "get", url: "/api/temperature", params });
  };

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateTime = (rowData) => {
    if (!rowData.date) return "";
    const d = new Date(rowData.date);
    return d.toLocaleString("ru-RU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const tempBodyTemplate = (rowData) => {
    const temp = parseFloat(rowData.temp);
    let className = "";
    if (temp > 40) className = "temp-critical";
    else if (temp > 35) className = "temp-warning";
    else if (temp > 30) className = "temp-elevated";
    return <span className={className}>{rowData.temp}</span>;
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const leftToolbarTemplate = () => {
    return (
      <div className="flex align-items-center gap-2">
        <Calendar
          value={date1}
          onChange={(e) => setDate1(e.value)}
          placeholder="Дата с"
          dateFormat="yy-mm-dd"
          showIcon
          className="p-inputtext-sm"
        />
        <Calendar
          value={date2}
          onChange={(e) => setDate2(e.value)}
          placeholder="Дата по"
          dateFormat="yy-mm-dd"
          showIcon
          className="p-inputtext-sm"
        />
        <Button
          label="Показать"
          icon="pi pi-search"
          className="p-button-primary p-button-sm"
          onClick={handleSearch}
        />
      </div>
    );
  };

  const rightToolbarTemplate = () => {
    return (
      <div className="flex align-items-center gap-2">
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Поиск..."
          className="p-inputtext-sm"
        />
        <Button
          label="CSV"
          icon="pi pi-file"
          className="p-button-success p-button-sm"
          onClick={exportCSV}
          disabled={!data || data.length === 0}
        />
      </div>
    );
  };

  if (!user) return null;

  return (
    <div className="flex justify-content-center">
      <div style={{ width: "100%", maxWidth: "1400px", padding: "1rem" }}>
        <Toast ref={toast} position="top-right" />

        <Toolbar
          className="mb-4"
          left={leftToolbarTemplate}
          right={rightToolbarTemplate}
        />

        {loading ? (
          <ProgressBar
            mode="indeterminate"
            style={{ height: "6px" }}
          />
        ) : (
          <DataTable
            ref={dt}
            value={data}
            paginator
            rows={50}
            rowsPerPageOptions={[25, 50, 100, 500]}
            filters={filters}
            globalFilterFields={["btsName", "region", "temp", "humidity"]}
            emptyMessage="Нет данных"
            size="small"
            showGridlines
            stripedRows
            sortMode="multiple"
            removableSort
            scrollable
            scrollHeight="650px"
            exportFilename="temperature_data"
            style={{ fontSize: "0.85rem" }}
            header="Температура и влажность на БТС"
          >
            <Column
              field="btsName"
              header="BTS"
              sortable
              filter
              filterPlaceholder="Поиск BTS"
              style={{ minWidth: "150px" }}
            />
            <Column
              field="region"
              header="Регион"
              sortable
              filter
              filterPlaceholder="Поиск региона"
              style={{ minWidth: "120px" }}
            />
            <Column
              field="date"
              header="Дата"
              sortable
              body={formatDateTime}
              style={{ minWidth: "150px" }}
            />
            <Column
              field="temp"
              header="Температура"
              sortable
              body={tempBodyTemplate}
              style={{ minWidth: "120px" }}
            />
            <Column
              field="humidity"
              header="Влажность"
              sortable
              style={{ minWidth: "100px" }}
            />
          </DataTable>
        )}
      </div>

      <style>{`
        .temp-critical { background-color: #ff4444; color: white; padding: 2px 8px; border-radius: 4px; }
        .temp-warning { background-color: orange; color: white; padding: 2px 8px; border-radius: 4px; }
        .temp-elevated { background-color: #ffeb3b; padding: 2px 8px; border-radius: 4px; }
      `}</style>
    </div>
  );
};

export default Temperature;
